import { useState } from 'react';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { QueryBuilder } from '../components/analytics/QueryBuilder.tsx';
import { MetricCard } from '../components/analytics/MetricCard.tsx';
import { AnalyticsChart } from '../components/analytics/AnalyticsChart.tsx';
import {
  useMetrics,
  useAnalyticsQuery,
  type MetricDefinition,
  type AnalyticsQueryResult,
} from '../hooks/useAnalyticsQuery.ts';
import { getToken } from '../lib/auth.ts';

export function AnalyticsPage() {
  const token = getToken();
  const { data: metrics = [] } = useMetrics(token ?? '');
  const [result, setResult] = useState<AnalyticsQueryResult | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<MetricDefinition | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { mutate: runQuery, isPending } = useAnalyticsQuery();

  function handleQuery(input: Parameters<typeof runQuery>[0]) {
    const metricDef = metrics.find((m) => m.id === input.metric) ?? null;
    setSelectedMetric(metricDef);
    setError(null);

    runQuery(input, {
      onSuccess: setResult,
      onError: (err) => setError(err.message),
    });
  }

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Analytics"
        description="Esplora i dati di email, web tracking e loyalty"
      />

      <QueryBuilder metrics={metrics} loading={isPending} onSubmit={handleQuery} />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">
          {error}
        </div>
      )}

      {(result || isPending) && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <MetricCard
              label="Totale"
              value={result?.summary.total ?? null}
              unit={selectedMetric?.unit ?? 'count'}
              deltaPct={result?.summary.delta_pct}
              loading={isPending}
            />
            <MetricCard
              label="Media per periodo"
              value={result?.summary.average ?? null}
              unit={selectedMetric?.unit ?? 'count'}
              loading={isPending}
            />
            <MetricCard
              label="Tempo query (ms)"
              value={result?.meta.query_execution_ms ?? null}
              unit="count"
              loading={isPending}
            />
            <MetricCard
              label="MB scansionati"
              value={result?.meta.scanned_mb ?? null}
              unit="count"
              loading={isPending}
            />
          </div>

          {/* Chart */}
          <AnalyticsChart
            series={result?.series ?? []}
            comparison={result?.comparison}
            unit={selectedMetric?.unit ?? 'count'}
            loading={isPending}
            scannedMb={result?.meta.scanned_mb}
            executionMs={result?.meta.query_execution_ms}
          />
        </>
      )}
    </div>
  );
}
